class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// =============================================
// SHIPPING PROTECTION ADDON
// =============================================

const SHIPPING_PROTECTION_VARIANT_ID = 45902140571809;
const SHIPPING_PROTECTION_PRODUCT_ID = 8482125250721;

async function getCart() {
  const res = await fetch('/cart.js');
  return res.json();
}

function calcProtectionQty(subtotalCents) {
  // 3% of subtotal in cents, rounded to nearest cent (quantity = cents)
  return Math.round(subtotalCents * 0.03);
}

async function removeProtection(cart) {
  const item = cart.items.find(i => i.product_id === SHIPPING_PROTECTION_PRODUCT_ID);
  if (!item) return;
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: item.key, quantity: 0 }),
  });
}

async function addOrUpdateProtection(subtotalCents) {
  const qty = calcProtectionQty(subtotalCents);
  if (qty < 1) return;

  await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: SHIPPING_PROTECTION_VARIANT_ID,
      quantity: qty,
    }),
  });
}

async function refreshCartDrawer() {
  const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
  const text = await res.text();
  const html = new DOMParser().parseFromString(text, 'text/html');

  const newItems = html.querySelector('cart-drawer-items');
  const newFooter = html.querySelector('.drawer__footer');
  const currentItems = document.querySelector('cart-drawer-items');
  const currentFooter = document.querySelector('#CartDrawer .drawer__footer');

  if (newItems && currentItems) currentItems.replaceWith(newItems);
  if (newFooter && currentFooter) currentFooter.replaceWith(newFooter);

  // Update cart bubble
  const res2 = await fetch(`/?section_id=cart-icon-bubble`);
  const text2 = await res2.text();
  const html2 = new DOMParser().parseFromString(text2, 'text/html');
  const newBubble = html2.querySelector('.shopify-section');
  const currentBubble = document.getElementById('cart-icon-bubble');
  if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML;

  initShippingProtectionToggle();
}

async function handleProtectionToggle(enabled) {
  const toggle = document.getElementById('ShippingProtectionToggle');
  if (toggle) toggle.disabled = true;

  const cart = await getCart();

  // Subtotal excluding protection product
  const subtotal = cart.items
    .filter(i => i.product_id !== SHIPPING_PROTECTION_PRODUCT_ID)
    .reduce((sum, i) => sum + i.line_price, 0);

  if (enabled) {
    await removeProtection(cart); // remove first to avoid duplicate
    await addOrUpdateProtection(subtotal);
  } else {
    await removeProtection(cart);
  }

  await refreshCartDrawer();
}

// Called after every cart quantity change to keep protection in sync
async function syncProtectionWithCart() {
  const cart = await getCart();

  const protectionItem = cart.items.find(i => i.product_id === SHIPPING_PROTECTION_PRODUCT_ID);
  if (!protectionItem) return; // toggle is OFF, nothing to sync

  const subtotal = cart.items
    .filter(i => i.product_id !== SHIPPING_PROTECTION_PRODUCT_ID)
    .reduce((sum, i) => sum + i.line_price, 0);

  const correctQty = calcProtectionQty(subtotal);

  if (protectionItem.quantity !== correctQty) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: protectionItem.key, quantity: correctQty }),
    });
    await refreshCartDrawer();
  }
}

function initShippingProtectionToggle() {
  const toggle = document.getElementById('ShippingProtectionToggle');
  if (!toggle) return;

  const fresh = toggle.cloneNode(true);
  toggle.parentNode.replaceChild(fresh, toggle);

  fresh.addEventListener('change', (e) => {
    handleProtectionToggle(e.target.checked);
  });
}

// Hook into existing cart quantity changes
const _originalUpdateQuantity = CartItems.prototype.updateQuantity;
CartItems.prototype.updateQuantity = async function(...args) {
  _originalUpdateQuantity.apply(this, args);
  // Wait for cart update to finish then sync protection
  setTimeout(syncProtectionWithCart, 1500);
};

document.addEventListener('DOMContentLoaded', initShippingProtectionToggle);